import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, PlayCircle, RefreshCw } from "lucide-react";
import Layout from "../../components/Layout/Layout";
import UpgradeBanner from "../../components/Billing/UpgradeBanner";
import AnnouncementBanner from "../../components/AnnouncementBanner/AnnouncementBanner";
import api from "../../services/api";
import ProgressBar from "./ProgressBar";
import { subjectStyle, errMsg, classQuery } from "./courseUtils";
import "./courses.css";

const CourseCatalog = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const cls = params.get("class") || "";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .get("/api/courses", { params: cls ? { class: cls } : {} })
      .then((res) => !cancelled && setData(res.data))
      .catch((e) => !cancelled && setError(errMsg(e, "We couldn't load your courses.")))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [cls, reloadKey]);

  const retry = useCallback(() => setReloadKey((k) => k + 1), []);

  const activeClass = data?.class || cls;
  const classes = data?.classes?.length ? data.classes : ["JSS1", "JSS2", "JSS3", "SS1", "SS2", "SS3"];
  const cont = data?.continue;

  return (
    <Layout fullBleed>
      <div className="courses-page">
        <div className="cs-shell">
          <header className="cs-head">
            <div>
              <h1 className="cs-title">Courses</h1>
              <p className="cs-subtitle">Learn one chapter at a time. Pass each short quiz to unlock the next chapter.</p>
            </div>
            <div className="cs-pills" role="group" aria-label="Choose class">
              {classes.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`cs-pill ${c === activeClass ? "active" : ""}`}
                  aria-pressed={c === activeClass}
                  onClick={() => setParams({ class: c })}
                >
                  {c}
                </button>
              ))}
            </div>
          </header>

          <AnnouncementBanner />
          {!loading && data?.reviews?.due > 0 && data.reviews.next && (
            <aside className="cs-reviewbar" role="status" aria-label="Quick reviews">
              <RefreshCw size={22} aria-hidden="true" />
              <div>
                <strong>{data.reviews.due} quick review{data.reviews.due === 1 ? "" : "s"} waiting</strong>
                <span>Starting with “{data.reviews.next.title}” ({data.reviews.next.subject}). About 5 minutes, and it helps you remember it.</span>
              </div>
              <button type="button" className="eg-btn eg-btn-accent" onClick={() => navigate(data.reviews.next.path)}>Review now</button>
            </aside>
          )}
          {!loading && <UpgradeBanner billing={data?.billing} />}

          {error && (
            <div className="cs-alert" role="alert">
              <span>{error}</span>
              <button type="button" className="eg-btn eg-btn-outline" onClick={retry}>Retry</button>
            </div>
          )}

          {cont && !loading && (
            <section className="cs-continue" aria-label="Continue learning">
              <div className="cs-continue-text">
                <span className="cs-eyebrow-light">Continue learning</span>
                <h2>{cont.subject} › {cont.topic_title}</h2>
                <p>
                  {cont.chapter_title}
                  {cont.position ? ` · ${cont.position}` : ""}
                </p>
              </div>
              <button
                type="button"
                className="eg-btn eg-btn-accent"
                onClick={() => navigate(`/courses/${cont.slug}/${cont.chapter_id}${classQuery(cont.class)}`)}
              >
                <PlayCircle size={18} /> Continue
              </button>
            </section>
          )}

          {loading ? (
            <div className="cs-grid" aria-busy="true">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="cs-card cs-skel-card">
                  <div className="cs-skel cs-skel-icon" />
                  <div className="cs-skel cs-skel-line" />
                  <div className="cs-skel cs-skel-line short" />
                </div>
              ))}
            </div>
          ) : (
            <div className="cs-grid">
              {(data?.subjects || []).map((s) => {
                const { Icon, color } = subjectStyle(s.subject);
                const total = s.chapters_total;
                const done = total != null && total > 0 && s.chapters_passed >= total;
                const action = done ? "Completed ✓" : s.started ? "Continue" : "Start";
                return (
                  <Link
                    key={s.slug}
                    to={`/courses/${s.slug}${classQuery(activeClass)}`}
                    className="cs-card cs-subject"
                  >
                    <span className="cs-subject-icon" style={{ background: color }}>
                      <Icon size={24} />
                    </span>
                    <h3 className="cs-subject-name">{s.subject}</h3>
                    <p className="cs-muted">{total != null ? `${total} chapters` : "Start learning"}</p>
                    {s.started && total != null && (
                      <div className="cs-subject-progress">
                        <ProgressBar value={s.percent ?? 0} label={`${s.subject} progress`} small />
                        <span className="cs-muted-sm">{s.chapters_passed} of {total} chapters</span>
                      </div>
                    )}
                    <span className={`cs-subject-action ${done ? "done" : ""}`}>
                      {done ? <CheckCircle2 size={16} /> : null}
                      {action}
                      {!done && <ArrowRight size={16} />}
                    </span>
                  </Link>
                );
              })}
              {!error && !(data?.subjects || []).length && (
                <p className="cs-muted">No subjects available for this class yet.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CourseCatalog;
